import { getDatabase, ref, get } from 'firebase/database';
import { getCachedData, setCachedData, buildCacheKey } from '../utils/cache';

const CACHE_TTL = 7 * 24 * 60 * 60 * 1000;

async function fetchFromRTDB(path) {
  const rtdb = getDatabase();
  const snapshot = await get(ref(rtdb, path));
  if (!snapshot.exists()) return null;
  return snapshot.val();
}

export async function getUniversidadesFromRTDB() {
  const cacheKey = buildCacheKey('universidades_exani_ii');
  
  const cached = getCachedData(cacheKey, CACHE_TTL);
  if (cached !== null) {
    return cached;
  }

  try {
    const data = await fetchFromRTDB('universidades_exani_ii');
    if (!data) return [];

    const universidades = [];
    Object.entries(data).forEach(([siglas, uni]) => {
      if (siglas === '_metadata') return;
      if (!uni || typeof uni !== 'object') return;
      if (!uni.nombre || !uni.siglas) return;
      
      universidades.push({
        id: siglas,
        siglas: uni.siglas,
        nombre: uni.nombre,
        tipo: uni.tipo || '',
        estado: uni.estado || '',
        region: uni.region || '',
        sede_principal: uni.sede_principal || '',
        total_carreras: uni.total_carreras || 0,
        carreras: uni.carreras ? Object.entries(uni.carreras).map(([idx, carrera]) => ({
          index: parseInt(idx),
          nombre: carrera.nombre || '',
          area: carrera.area || '',
          modulos_exani_ii: carrera.modulos_exani_ii || []
        })) : [],
        enlaceOficial: uni.enlaceOficial || uni.portalOficial || uni.sitioWeb || getPortalOficial(uni.siglas, uni.nombre)
      });
    });

    const sorted = universidades.sort((a, b) => a.nombre.localeCompare(b.nombre));
    
    setCachedData(cacheKey, sorted, null);
    return sorted;
  } catch (error) {
    console.warn('[universidadesService] Error fetching universidades:', error);
    return [];
  }
}

function getPortalOficial(siglas, nombre) {
  const siglasUpper = (siglas || '').toUpperCase();
  const nombreLower = (nombre || '').toLowerCase();
  
  const portales = {
    'UNAM': 'https://www.unam.mx',
    'IPN': 'https://www.ipn.mx',
    'UAM': 'https://www.uam.mx',
    'UPN': 'https://www.upn.mx',
    'COLMEX': 'https://www.colmex.mx',
    'CIDE': 'https://www.cide.edu',
    'UADY': 'https://www.uady.mx',
    'UV': 'https://www.uv.mx',
    'UJAT': 'https://www.ujat.mx',
    'UAA': 'https://www.uaa.mx',
    'UABCS': 'https://www.uabcs.mx',
    'UACAM': 'https://www.uacam.mx',
    'UNACAR': 'https://www.unacar.mx',
    'UNACH': 'https://www.unach.mx',
    'UNICACH': 'https://www.unicach.mx',
    'UANL': 'https://www.uanl.mx',
    'UAT': 'https://www.uat.edu.mx',
    'UACH': 'https://www.uach.mx',
    'UACJ': 'https://www.uacj.mx',
    'UJED': 'https://www.ujed.mx',
    'UNISON': 'https://www.unison.mx',
    'UABC': 'https://www.uabc.mx',
    'UAS': 'https://www.uas.edu.mx',
    'ITSON': 'https://www.itson.mx',
    'BUAP': 'https://www.buap.mx',
    'UAEH': 'https://www.uaeh.edu.mx',
    'UAEMEX': 'https://www.uaemex.mx',
    'UAEM': 'https://www.uaem.mx',
    'UATX': 'https://www.uatx.mx',
    'UAQ': 'https://www.uaq.mx',
    'UASLP': 'https://www.uaslp.mx',
    'UAZ': 'https://www.uaz.edu.mx',
    'UG': 'https://www.ugto.mx',
    'UDEG': 'https://www.udg.mx',
    'UMSNH': 'https://www.umich.mx',
    'UCOL': 'https://www.ucol.mx',
    'UAN': 'https://www.uan.mx',
    'UABJO': 'https://www.uabjo.mx',
    'UAGRO': 'https://www.uagro.mx',
    'UTM': 'https://www.utm.mx',
    'UACh': 'https://www.uaach.mx',
    'UAAAN': 'https://www.uaaan.mx',
    'ITESM': 'https://www.tec.mx',
    'IBERO': 'https://www.ibero.mx',
    'ITAM': 'https://www.itam.mx',
    'ANAHUAC': 'https://www.anahuac.mx',
    'UP': 'https://www.up.edu.mx',
    'UDLAP': 'https://www.udlap.mx',
    'ITESO': 'https://www.iteso.mx',
    'UDEM': 'https://www.udem.mx',
    'LA SALLE': 'https://www.lasalle.mx',
    'UVM': 'https://www.uvm.mx',
    'UNITEC': 'https://www.unitec.mx',
    'TECMILENIO': 'https://www.tecmilenio.mx',
    'UAG': 'https://www.uag.mx',
    'UPAEP': 'https://www.upaep.mx',
    'UIC': 'https://www.uic.edu.mx',
    'USB': 'https://www.usb.mx',
    'EBC': 'https://www.ebc.mx',
    'UCSJ': 'https://www.ucsj.mx',
    'WESTHILL': 'https://www.westhill.edu.mx',
    'UNILA': 'https://www.unila.edu.mx',
    'UI': 'https://www.universidadinsurgentes.edu.mx',
    'UNIMEX': 'https://www.unimex.mx',
    'UDEC': 'https://www.udec.edu.mx',
    'UCC': 'https://www.ucc.mx',
    'UX': 'https://www.ux.edu.mx'
  };

  return portales[siglasUpper] || `https://www.google.com/search?q=${encodeURIComponent(nombre + ' convocatoria EXANI-II')}`;
}

export async function searchUniversidades(query) {
  const universidades = await getUniversidadesFromRTDB();
  const q = query.toLowerCase().trim();
  if (!q) return universidades;
  
  return universidades.filter(u => 
    u.nombre.toLowerCase().includes(q) ||
    u.siglas.toLowerCase().includes(q) ||
    u.estado.toLowerCase().includes(q) ||
    u.region.toLowerCase().includes(q)
  );
}

export async function getUniversidadById(id) {
  const universidades = await getUniversidadesFromRTDB();
  return universidades.find(u => u.id === id) || null;
}

export async function getUniversidadesByEstado(estado) {
  const universidades = await getUniversidadesFromRTDB();
  return universidades.filter(u => u.estado.toLowerCase() === estado.toLowerCase());
}

export async function getUniversidadesByRegion(region) {
  const universidades = await getUniversidadesFromRTDB();
  return universidades.filter(u => u.region.toLowerCase() === region.toLowerCase());
}